import moment from "moment";
import React from "react";

import { useCurrentUser } from "./use-current-user";
import PremiumFeatureGate from "../components/common/premium/PremiumFeatureGate";

interface Permission {
  permissionName: string;
  isEnabled: boolean;
}

interface GateOptions {
  featureName?: string;
  iconPosition?: string;
  iconSize?: string;
  requiredPermission?: string;
}

interface RenderPremiumContentProps {
  premiumContent: React.ReactNode;
  freeContent?: React.ReactNode;
  gateOptions: GateOptions;
  requiredPermission?: string;
}

// Matches the permissions in the UserPermissions from BE
export const UserPermissions = {
  TEAMS_ENABLED: "scribenote:teams",
  BASIC_TEMPLATES_ENABLED: "scribenote:basic_templates",
  ADAPTIVE_TEMPLATES_ENABLED: "scribenote:adaptive_templates",
  CUSTOM_DEFAULTS_ENABLED: "scribenote:custom_defaults",
  CLIENT_SUMMARY_ENABLED: "scribenote:client_summary",
  WRITING_ASSISTANT_ENABLED: "scribenote:writing_assistant",
  MULTILINGUAL_ENABLED: "scribenote:multilingual",
  POINT_FORM_AP_FIELDS_ENABLED: "scribenote:point_form_ap_fields",
  NAME_CORRECTION_ENABLED: "scribenote:name_correction",
  DESKTOP_APP_ENABLED: "scribenote:desktop_app",
  PREMIUM_TRANSCRIPT_ENABLED: "scribenote:premium_transcript",
  PREMIUM_DEFAULT_NOTES_ENABLED: "scribenote:premium_default_notes",
  HUMAN_SUPPORT_ENABLED: "scribenote:human_support",
  SCRIBEPHONE_ENABLED: "scribenote:scribephone",
  NOTE_FEEDBACK_ENABLED: "scribenote:note_feedback",
  CHANGE_EMAIL_ENABLED: "scribenote:change_email",
};

export function usePremiumFeature() {
  const { data: userData } = useCurrentUser();
  const userPermissions = (userData?.user?.userPermissions ||
    []) as Permission[];
  const hasPaidSubscription =
    userData?.user?.account?.accountType === "unlimited" ||
    moment(
      userData?.user?.account?.freeTrialExpiresAt as string | null,
    ).isAfter(moment());

  const hasPermission = (permissionName: string) => {
    return userPermissions.some(
      (permission: Permission | null) =>
        permission?.permissionName === permissionName &&
        permission?.isEnabled,
    );
  };

  const hasFeatureAccess = (featurePermission?: string) => {
    // If no specific permission provided, fall back to subscription check
    if (!featurePermission) {
      return hasPaidSubscription;
    }

    return hasPermission(featurePermission);
  };

  const gateComponentIfNeeded = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component: React.ComponentType<any>,
    options?: GateOptions,
  ) => {
    if (!hasFeatureAccess(options?.requiredPermission)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const GatedComponent = (props: any) => (
        <PremiumFeatureGate
          featureName={options?.featureName}
          iconPosition={options?.iconPosition}
          iconSize={options?.iconSize}
          requiredPermission={options?.requiredPermission}
        >
          <Component {...props} />
        </PremiumFeatureGate>
      );
      GatedComponent.displayName = `PremiumFeatureGate(${
        Component.displayName || Component.name || "Component"
      })`;
      return GatedComponent;
    }

    return Component;
  };

  const renderPremiumContent = ({
    premiumContent,
    freeContent = null,
    gateOptions,
    requiredPermission,
  }: RenderPremiumContentProps) => {
    if (hasFeatureAccess(requiredPermission)) {
      return premiumContent;
    }

    if (freeContent) {
      return freeContent;
    }

    return (
      <PremiumFeatureGate
        featureName={gateOptions.featureName}
        iconPosition={gateOptions.iconPosition}
        requiredPermission={requiredPermission}
      >
        {premiumContent}
      </PremiumFeatureGate>
    );
  };

  return {
    hasPaidSubscription,
    hasPermission,
    hasFeatureAccess,
    gateComponentIfNeeded,
    renderPremiumContent,
    PremiumFeatureGate,
    UserPermissions,
  };
}
